import { useCallback, useEffect, useMemo, useRef } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import Banner from '~/components/banner/Banner';
import CmsTransformer from '~/components/cms/CmsTransformer';
import ProductsSlider from '~/components/product/ProductsSlider';
import ReviewsBlock from '~/components/review/ReviewsBlock';
import TaxonCard from '~/components/taxons/TaxonCard';
import Usp from '~/components/usp/Usp';
import { useViewPromotion, pushSelectPromotion, createPromotionItem, type ListContext } from '~/gtm';
import type { GA4PromotionItem } from '~/gtm/types';
import Layout from '~/layouts/Default';
import { CmsItem } from '~/types/Cms';
import { HomepageSettings } from '~/types/Homepage';
import { HighlightedTaxon } from '~/types/Taxon';

interface Props {
  settings?: HomepageSettings;
  apiUrl?: string;
}

const Homepage = ({ settings }: Props) => {
  const { t } = useTranslation();
  const tiles = settings?.mainBanners?.settings;
  const productsSlider1 = settings?.productsSlider1;
  const productsSlider2 = settings?.productsSlider2;
  const categoryHighlights = settings?.categoryHighlights?.settings;
  const informationContent = settings?.informationBlock?.settings?.content;
  const promotionalBanners = settings?.promotionalBanners;
  const reviews = settings?.reviews ?? [];

  // GTM: List context for product sliders
  const slider1ListContext: ListContext = {
    item_list_id: 'new_additions',
    item_list_name: productsSlider1?.title || 'New additions',
  };
  const slider2ListContext: ListContext = {
    item_list_id: 'view_our_offer',
    item_list_name: productsSlider2?.title || 'View our offer',
  };
  const defaultCurrency = productsSlider1?.products?.[0]?.defaultVariantData?.currency || 'CZK';

  // GTM: Track promotion views with IntersectionObserver
  const { observePromotion } = useViewPromotion();

  // Store refs for main tiles
  const tileRefsRef = useRef<Map<number, HTMLAnchorElement | null>>(new Map());
  const promotionRefsRef = useRef<Map<number, HTMLDivElement | null>>(new Map());

  // Create promotion items for main tiles
  const mainTilePromotions: GA4PromotionItem[] = useMemo(
    () => tiles?.map((tile, index) => createPromotionItem(tile, 'homepage_main_banner', index)) || [],
    [tiles]
  );

  // Create promotion items for promotional banners
  const promoBannerPromotions: GA4PromotionItem[] = useMemo(
    () =>
      promotionalBanners?.map((banner, index) =>
        createPromotionItem(
          { title: banner.title || `Promo Banner ${index + 1}`, link: banner.link },
          'homepage_promo_banner',
          index
        )
      ) || [],
    [promotionalBanners]
  );

  // Observe main tiles
  useEffect(() => {
    const cleanups: (() => void)[] = [];
    tileRefsRef.current.forEach((element, index) => {
      if (element && mainTilePromotions[index]) {
        const cleanup = observePromotion(element, mainTilePromotions[index]);
        if (cleanup) cleanups.push(cleanup);
      }
    });
    return () => cleanups.forEach((fn) => fn());
  }, [tiles, observePromotion, mainTilePromotions]);

  // Observe promotional banners
  useEffect(() => {
    const cleanups: (() => void)[] = [];
    promotionRefsRef.current.forEach((element, index) => {
      if (element && promoBannerPromotions[index]) {
        const cleanup = observePromotion(element, promoBannerPromotions[index]);
        if (cleanup) cleanups.push(cleanup);
      }
    });
    return () => cleanups.forEach((fn) => fn());
  }, [promotionalBanners, observePromotion, promoBannerPromotions]);

  // Handle promotion click
  const handleTileClick = useCallback(
    (index: number) => {
      if (mainTilePromotions[index]) {
        pushSelectPromotion(mainTilePromotions[index]);
      }
    },
    [mainTilePromotions]
  );

  const handlePromoBannerClick = useCallback(
    (index: number) => {
      if (promoBannerPromotions[index]) {
        pushSelectPromotion(promoBannerPromotions[index]);
      }
    },
    [promoBannerPromotions]
  );

  return (
    <Layout>
      <Container>
        <div className="homepage-tiles custom_scrollbar">
          {tiles?.map((tile, index) => (
            <a
              ref={(el) => tileRefsRef.current.set(index, el)}
              className={`homepage-tile homepage-tile--${index + 1}`}
              key={index}
              href={tile.link}
              aria-label={tile.title}
              onClick={() => handleTileClick(index)}
            >
              <picture>
                <source
                  media="(min-width: 768px)"
                  srcSet={tile.desktopImageSet?.srcset ?? tile.desktopImage}
                  sizes={tile.desktopImageSet ? '100vw' : undefined}
                />
                <img
                  src={tile.mobileImageSet?.src ?? tile.mobileImage}
                  srcSet={tile.mobileImageSet?.srcset}
                  sizes={tile.mobileImageSet ? '100vw' : undefined}
                  alt={tile.title}
                  className="homepage-tile__image"
                  // Intrinsic size of the mobile crop; CSS (position: absolute, 100%/100%, object-fit: cover)
                  // fully overrides the rendered box, these only stop it from shifting before that CSS applies.
                  width={320}
                  height={436}
                  fetchpriority={index === 0 ? 'high' : 'low'}
                  loading={index <= 1 ? 'eager' : 'lazy'}
                  decoding="async"
                />
              </picture>
              <div className="homepage-tile__content d-flex flex-column">
                {tile.subtitle && index < 2 && <span className="homepage-tile__subtitle">{tile.subtitle}</span>}
                <strong className="homepage-tile__title">{tile.title}</strong>
                {tile.subtitle && index > 1 && <span className="homepage-tile__description">{tile.subtitle}</span>}
                {tile.buttonTitle && <span className="btn btn-brand">{tile.buttonTitle}</span>}
              </div>
            </a>
          ))}
        </div>
        <Usp />
        {productsSlider1 && productsSlider1.products.length > 0 && (
          <ProductsSlider
            title={productsSlider1.title}
            description={productsSlider1.subtitle}
            products={productsSlider1.products}
            banner={productsSlider1.banner}
            listContext={slider1ListContext}
            currency={defaultCurrency}
            centeredHeader={false}
          />
        )}
        {categoryHighlights && (categoryHighlights.tiles.length > 0 || categoryHighlights.categories.length > 0) && (
          <section className="homepage__taxons">
            <h3 className="homepage__taxons__title">{t('pages.homepage.taxons.title')}</h3>
            <Row className="homepage__taxons__cards custom_scrollbar g-3">
              {categoryHighlights.tiles.slice(0, 8).map((taxon: HighlightedTaxon, index: number) => (
                <Col xs={3} className="homepage__taxon_card" key={index}>
                  <TaxonCard taxon={taxon} />
                </Col>
              ))}
            </Row>
          </section>
        )}
        <div className="information-block">
          {informationContent?.map((item: CmsItem, index: number) => (
            <CmsTransformer item={item} key={index} />
          ))}
        </div>
        <ReviewsBlock reviews={reviews} />
        {productsSlider2 && productsSlider2.products.length > 0 && (
          <ProductsSlider
            title={productsSlider2.title}
            description={productsSlider2.subtitle}
            products={productsSlider2.products}
            banner={productsSlider2.banner}
            listContext={slider2ListContext}
            currency={defaultCurrency}
          />
        )}
        {promotionalBanners && promotionalBanners.length > 0 && (
          <Row className="mb-5">
            {promotionalBanners.map(
              (banner, index) =>
                // Hide a banner with no image — nothing meaningful left to render.
                (banner.desktopImage || banner.mobileImage) && (
                  <Col md={6} key={index}>
                    <div ref={(el) => promotionRefsRef.current.set(index, el)}>
                      <Banner
                        image={{
                          desktop: banner.desktopImage,
                          mobile: banner.mobileImage,
                          desktopSet: banner.desktopImageSet,
                          mobileSet: banner.mobileImageSet,
                        }}
                        link={banner.link}
                        title={banner.title}
                        subtitle={banner.subtitle}
                        buttonTitle={banner.buttonTitle}
                        onClick={() => handlePromoBannerClick(index)}
                      />
                    </div>
                  </Col>
                )
            )}
          </Row>
        )}
      </Container>
    </Layout>
  );
};

export default Homepage;
